'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Code2, GitBranch, Users, Clock, Zap, CheckCircle2, ArrowUpRight, ArrowDownRight, RefreshCw, Download, Monitor, Globe, Database, Server, Shield, Star, TrendingUp, Play, Pause, Package, Cpu, Cloud, Lock, BarChart2, Activity,  } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area,  } from 'recharts';

// ── Data ──────────────────────────────────────────────────────────────────────

const kpis = [
  { label: 'Active Projects', value: '14', change: '+2', up: true, icon: GitBranch, color: 'text-primary', bg: 'bg-primary/10' },
  { label: 'Team Capacity', value: '78%', change: '+5%', up: true, icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
  { label: 'On-Time Delivery', value: '91%', change: '+3%', up: true, icon: CheckCircle2, color: 'text-success', bg: 'bg-success/10' },
  { label: 'Avg. Sprint Velocity', value: '42 pts', change: '+8 pts', up: true, icon: Zap, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  { label: 'Bugs Resolved', value: '238', change: '+18', up: true, icon: Shield, color: 'text-violet-400', bg: 'bg-violet-400/10' },
  { label: 'Client Satisfaction', value: '4.8/5', change: '+0.2', up: true, icon: Star, color: 'text-rose-400', bg: 'bg-rose-400/10' },
];

const activeProjects = [
  {
    id: 1,
    name: 'ArrowsGroups Corporate Portal',
    client: 'Arrows Groups Internal',
    type: 'Web App',
    status: 'In Progress',
    progress: 72,
    sprint: 'Sprint 8',
    deadline: 'Sep 12, 2026',
    team: 5,
    priority: 'High',
    stack: ['Next.js', 'TypeScript', 'Supabase', 'Tailwind'],
    daysLeft: 28,
  },
  {
    id: 2,
    name: 'E-Commerce Platform Rebuild',
    client: 'RetailEdge Co.',
    type: 'E-Commerce',
    status: 'In Progress',
    progress: 55,
    sprint: 'Sprint 5',
    deadline: 'Oct 3, 2026',
    team: 4,
    priority: 'High',
    stack: ['React', 'Node.js', 'PostgreSQL', 'Stripe'],
    daysLeft: 49,
  },
  {
    id: 3,
    name: 'Healthcare Patient Dashboard',
    client: 'MedCore Systems',
    type: 'Dashboard',
    status: 'In Progress',
    progress: 88,
    sprint: 'Sprint 11',
    deadline: 'Aug 28, 2026',
    team: 3,
    priority: 'Critical',
    stack: ['Vue.js', 'FastAPI', 'MySQL', 'Docker'],
    daysLeft: 13,
  },
  {
    id: 4,
    name: 'Real Estate Listing Portal',
    client: 'PropVista Ltd.',
    type: 'Web App',
    status: 'Review',
    progress: 95,
    sprint: 'Sprint 12',
    deadline: 'Aug 22, 2026',
    team: 3,
    priority: 'Medium',
    stack: ['Next.js', 'Prisma', 'MongoDB', 'Mapbox'],
    daysLeft: 7,
  },
  {
    id: 5,
    name: 'SaaS Analytics Platform',
    client: 'DataPulse Inc.',
    type: 'SaaS',
    status: 'In Progress',
    progress: 38,
    sprint: 'Sprint 3',
    deadline: 'Nov 15, 2026',
    team: 6,
    priority: 'High',
    stack: ['React', 'Python', 'Redis', 'AWS'],
    daysLeft: 92,
  },
  {
    id: 6,
    name: 'Mobile Banking Web App',
    client: 'FinTrust Bank',
    type: 'FinTech',
    status: 'On Hold',
    progress: 41,
    sprint: 'Sprint 4',
    deadline: 'Dec 1, 2026',
    team: 4,
    priority: 'Medium',
    stack: ['Angular', 'Java Spring', 'Oracle', 'Kubernetes'],
    daysLeft: 108,
  },
];

const techStack = [
  { category: 'Frontend', icon: Monitor, color: 'text-primary', bg: 'bg-primary/10', techs: [
    { name: 'Next.js 15', level: 95, projects: 8 },
    { name: 'React 19', level: 92, projects: 11 },
    { name: 'Vue.js 3', level: 78, projects: 4 },
    { name: 'Angular 17', level: 70, projects: 3 },
    { name: 'TypeScript', level: 96, projects: 14 },
  ]},
  { category: 'Backend', icon: Server, color: 'text-cyan-400', bg: 'bg-cyan-400/10', techs: [
    { name: 'Node.js', level: 90, projects: 9 },
    { name: 'Python / FastAPI', level: 82, projects: 6 },
    { name: 'Java Spring', level: 74, projects: 3 },
    { name: 'Go', level: 65, projects: 2 },
    { name: 'GraphQL', level: 80, projects: 5 },
  ]},
  { category: 'Database', icon: Database, color: 'text-amber-400', bg: 'bg-amber-400/10', techs: [
    { name: 'PostgreSQL', level: 91, projects: 10 },
    { name: 'MongoDB', level: 85, projects: 7 },
    { name: 'MySQL', level: 80, projects: 5 },
    { name: 'Redis', level: 75, projects: 6 },
    { name: 'Supabase', level: 88, projects: 8 },
  ]},
  { category: 'DevOps & Cloud', icon: Cloud, color: 'text-violet-400', bg: 'bg-violet-400/10', techs: [
    { name: 'AWS', level: 83, projects: 7 },
    { name: 'Docker', level: 90, projects: 12 },
    { name: 'Kubernetes', level: 72, projects: 4 },
    { name: 'Vercel / Netlify', level: 95, projects: 10 },
    { name: 'GitHub Actions', level: 88, projects: 13 },
  ]},
];

const teamMembers = [
  { name: 'Alex Chen', role: 'Lead Full-Stack', avatar: 'AC', capacity: 90, projects: 3, skills: ['Next.js', 'Node.js', 'AWS'] },
  { name: 'Priya Sharma', role: 'Frontend Engineer', avatar: 'PS', capacity: 75, projects: 2, skills: ['React', 'TypeScript', 'Tailwind'] },
  { name: 'Marcus Webb', role: 'Backend Engineer', avatar: 'MW', capacity: 85, projects: 3, skills: ['Python', 'PostgreSQL', 'Docker'] },
  { name: 'Lena Kovacs', role: 'UI/UX + Frontend', avatar: 'LK', capacity: 60, projects: 2, skills: ['Vue.js', 'Figma', 'CSS'] },
  { name: 'James Okafor', role: 'DevOps Engineer', avatar: 'JO', capacity: 70, projects: 4, skills: ['Kubernetes', 'AWS', 'CI/CD'] },
  { name: 'Sofia Reyes', role: 'Full-Stack Dev', avatar: 'SR', capacity: 80, projects: 2, skills: ['Angular', 'Java', 'Oracle'] },
];

const deliveryTimeline = [
  { month: 'Mar', delivered: 3, planned: 3, velocity: 38 },
  { month: 'Apr', delivered: 4, planned: 4, velocity: 41 },
  { month: 'May', delivered: 3, planned: 4, velocity: 36 },
  { month: 'Jun', delivered: 5, planned: 5, velocity: 44 },
  { month: 'Jul', delivered: 4, planned: 4, velocity: 42 },
  { month: 'Aug', delivered: 2, planned: 3, velocity: 42 },
];

const serviceOfferings = [
  {
    title: 'Custom Web Applications',
    icon: Globe,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    description: 'End-to-end development of scalable web apps tailored to business workflows.',
    features: ['SPA & SSR architectures', 'API-first design', 'Role-based access control', 'Real-time features'],
    startingAt: '$8,500',
    timeline: '6–16 weeks',
    activeCount: 5,
  },
  {
    title: 'E-Commerce Solutions',
    icon: Package,
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/20',
    description: 'High-converting storefronts with payment integrations and inventory management.',
    features: ['Custom checkout flows', 'Multi-currency support', 'Inventory & order mgmt', 'Analytics dashboards'],
    startingAt: '$6,200',
    timeline: '4–10 weeks',
    activeCount: 2,
  },
  {
    title: 'SaaS Platform Development',
    icon: Cpu,
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
    border: 'border-violet-400/20',
    description: 'Multi-tenant SaaS products with subscription billing and admin panels.',
    features: ['Multi-tenancy architecture', 'Stripe billing integration', 'Usage analytics', 'White-label options'],
    startingAt: '$15,000',
    timeline: '12–24 weeks',
    activeCount: 2,
  },
  {
    title: 'API & Backend Services',
    icon: Server,
    color: 'text-cyan-400',
    bg: 'bg-cyan-400/10',
    border: 'border-cyan-400/20',
    description: 'Robust REST/GraphQL APIs, microservices, and third-party integrations.',
    features: ['RESTful & GraphQL APIs', 'Microservices design', 'Webhook integrations', 'Rate limiting & auth'],
    startingAt: '$4,000',
    timeline: '3–8 weeks',
    activeCount: 3,
  },
  {
    title: 'Performance & Optimization',
    icon: Zap,
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/20',
    description: 'Audit and optimize existing apps for speed, SEO, and Core Web Vitals.',
    features: ['Core Web Vitals audit', 'Bundle size reduction', 'CDN & caching setup', 'Database query tuning'],
    startingAt: '$2,500',
    timeline: '1–3 weeks',
    activeCount: 1,
  },
  {
    title: 'Security & Compliance',
    icon: Lock,
    color: 'text-rose-400',
    bg: 'bg-rose-400/10',
    border: 'border-rose-400/20',
    description: 'Security audits, penetration testing, and GDPR/HIPAA compliance implementation.',
    features: ['Vulnerability scanning', 'OWASP compliance', 'GDPR/HIPAA readiness', 'SSL & auth hardening'],
    startingAt: '$3,200',
    timeline: '2–4 weeks',
    activeCount: 1,
  },
];

const CHART_COLORS = { primary: '#6366f1', success: '#34d399', warning: '#fbbf24', cyan: '#22d3ee', violet: '#a78bfa' };

const statusConfig: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  'In Progress': { label: 'In Progress', cls: 'bg-primary/15 text-primary', icon: Play },
  'Review': { label: 'Review', cls: 'bg-amber-400/15 text-amber-400', icon: Activity },
  'On Hold': { label: 'On Hold', cls: 'bg-muted text-muted-foreground', icon: Pause },
  'Completed': { label: 'Completed', cls: 'bg-success/15 text-success', icon: CheckCircle2 },
};

const priorityConfig: Record<string, string> = {
  Critical: 'bg-danger/15 text-danger',
  High: 'bg-amber-400/15 text-amber-400',
  Medium: 'bg-cyan-400/15 text-cyan-400',
  Low: 'bg-muted text-muted-foreground',
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function WebDevelopmentPage() {
  const [activeTab, setActiveTab] = useState<'projects' | 'stack' | 'team' | 'services'>('projects');

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <Code2 size={22} className="text-primary" />
              Web Development
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Active projects, tech stack, team capacity, delivery timelines &amp; service offerings
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="flex items-center gap-1.5 bg-card border border-border text-muted-foreground px-3 py-2 rounded-lg text-sm hover:text-foreground hover:bg-muted transition-colors">
              <RefreshCw size={14} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button className="flex items-center gap-1.5 bg-card border border-border text-foreground px-3 py-2 rounded-lg text-sm font-medium hover:bg-muted transition-colors">
              <Download size={14} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={15} className={kpi.color} />
                </div>
                <span className={`text-xs flex items-center gap-0.5 font-medium ${kpi.up ? 'text-success' : 'text-danger'}`}>
                  {kpi.up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                  {kpi.change}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-2xs sm:text-xs text-muted-foreground mt-0.5 leading-tight">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-card border border-border rounded-xl p-1 mb-6 w-full sm:w-auto sm:inline-flex overflow-x-auto">
          {(['projects', 'stack', 'team', 'services'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab
                  ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab === 'projects' ? 'Active Projects' : tab === 'stack' ? 'Tech Stack' : tab === 'team' ? 'Team Capacity' : 'Services'}
            </button>
          ))}
        </div>

        {/* ── ACTIVE PROJECTS TAB ── */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {/* Delivery Timeline Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-2">
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <BarChart2 size={15} className="text-primary" />
                  Monthly Delivery vs Planned
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={deliveryTimeline} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="planned" fill="#1e293b" radius={[4, 4, 0, 0]} name="Planned" />
                    <Bar dataKey="delivered" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Delivered" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp size={15} className="text-success" />
                  Sprint Velocity Trend
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={deliveryTimeline}>
                    <defs>
                      <linearGradient id="velGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} domain={[30, 50]} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="velocity" stroke={CHART_COLORS.success} fill="url(#velGrad)" strokeWidth={2} name="Velocity (pts)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Projects Table — desktop */}
            <div className="bg-card border border-border rounded-xl overflow-hidden hidden md:block">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Active Projects ({activeProjects.length})</h3>
                <span className="text-xs text-muted-foreground">{activeProjects.filter(p => p.status === 'In Progress').length} in progress</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {['Project', 'Client', 'Type', 'Status', 'Priority', 'Progress', 'Sprint', 'Deadline', 'Team'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeProjects.map((p) => {
                      const sc = statusConfig[p.status];
                      const StatusIcon = sc.icon;
                      return (
                        <tr key={p.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-foreground text-sm">{p.name}</p>
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {p.stack.slice(0, 3).map((s) => (
                                  <span key={s} className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{s}</span>
                                ))}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.client}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.type}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${sc.cls}`}>
                              <StatusIcon size={10} />
                              {sc.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${priorityConfig[p.priority]}`}>{p.priority}</span>
                          </td>
                          <td className="px-4 py-3 min-w-[120px]">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${p.progress >= 80 ? 'bg-success' : p.progress >= 50 ? 'bg-primary' : 'bg-amber-400'}`}
                                  style={{ width: `${p.progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground w-8 text-right">{p.progress}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{p.sprint}</td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-xs text-foreground whitespace-nowrap">{p.deadline}</p>
                              <p className={`text-2xs ${p.daysLeft <= 14 ? 'text-danger' : 'text-muted-foreground'}`}>{p.daysLeft}d left</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Users size={12} className="text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{p.team}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Projects Cards — mobile */}
            <div className="md:hidden space-y-3">
              {activeProjects.map((p) => {
                const sc = statusConfig[p.status];
                const StatusIcon = sc.icon;
                return (
                  <div key={p.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <p className="font-semibold text-foreground text-sm">{p.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{p.client}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${sc.cls}`}>
                        <StatusIcon size={10} />
                        {sc.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${p.progress >= 80 ? 'bg-success' : p.progress >= 50 ? 'bg-primary' : 'bg-amber-400'}`}
                          style={{ width: `${p.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">{p.progress}%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.sprint} · {p.type}</span>
                      <span className={p.daysLeft <= 14 ? 'text-danger' : ''}>{p.daysLeft}d left — {p.deadline}</span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {p.stack.map((s) => (
                        <span key={s} className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TECH STACK TAB ── */}
        {activeTab === 'stack' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {techStack.map((cat) => (
              <div key={cat.category} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-lg ${cat.bg} flex items-center justify-center`}>
                    <cat.icon size={16} className={cat.color} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">{cat.category}</h3>
                </div>
                <div className="space-y-3">
                  {cat.techs.map((tech) => (
                    <div key={tech.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-foreground">{tech.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{tech.projects} projects</span>
                          <span className="text-xs font-semibold text-foreground">{tech.level}%</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${cat.color.replace('text-', 'bg-')}`}
                          style={{ width: `${tech.level}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── TEAM CAPACITY TAB ── */}
        {activeTab === 'team' && (
          <div className="space-y-4">
            {/* Capacity overview bar */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users size={15} className="text-primary" />
                Team Capacity Overview
              </h3>
              <div className="space-y-4">
                {teamMembers.map((m) => (
                  <div key={m.name} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-3 sm:w-56 flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{m.avatar}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{m.role}</p>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center gap-3">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            m.capacity >= 85 ? 'bg-danger' : m.capacity >= 70 ? 'bg-amber-400' : 'bg-success'
                          }`}
                          style={{ width: `${m.capacity}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold w-10 text-right flex-shrink-0 ${
                        m.capacity >= 85 ? 'text-danger' : m.capacity >= 70 ? 'text-amber-400' : 'text-success'
                      }`}>{m.capacity}%</span>
                    </div>
                    <div className="flex items-center gap-1 sm:w-24 flex-shrink-0">
                      <GitBranch size={12} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{m.projects} projects</span>
                    </div>
                    <div className="flex gap-1 flex-wrap sm:w-52">
                      {m.skills.map((s) => (
                        <span key={s} className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Capacity legend */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Available', range: '< 70%', color: 'bg-success', count: teamMembers.filter(m => m.capacity < 70).length },
                { label: 'Moderate', range: '70–84%', color: 'bg-amber-400', count: teamMembers.filter(m => m.capacity >= 70 && m.capacity < 85).length },
                { label: 'At Capacity', range: '≥ 85%', color: 'bg-danger', count: teamMembers.filter(m => m.capacity >= 85).length },
              ].map((item) => (
                <div key={item.label} className="bg-card border border-border rounded-xl p-4 text-center">
                  <div className={`w-3 h-3 rounded-full ${item.color} mx-auto mb-2`} />
                  <p className="text-2xl font-bold text-foreground">{item.count}</p>
                  <p className="text-xs font-medium text-foreground mt-0.5">{item.label}</p>
                  <p className="text-2xs text-muted-foreground">{item.range}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SERVICES TAB ── */}
        {activeTab === 'services' && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {serviceOfferings.map((svc) => (
              <div key={svc.title} className={`bg-card border ${svc.border} rounded-xl p-5 flex flex-col`}>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl ${svc.bg} flex items-center justify-center`}>
                    <svc.icon size={18} className={svc.color} />
                  </div>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {svc.activeCount} active
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">{svc.title}</h3>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{svc.description}</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 size={11} className="text-success flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-border pt-3 flex items-center justify-between">
                  <div>
                    <p className="text-2xs text-muted-foreground">Starting at</p>
                    <p className={`text-sm font-bold ${svc.color}`}>{svc.startingAt}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xs text-muted-foreground">Timeline</p>
                    <p className="text-xs font-medium text-foreground flex items-center gap-1">
                      <Clock size={10} />
                      {svc.timeline}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </AppLayout>
  );
}
